// SPDX-License-Identifier: Apache-2.0

// /dev/comms — fixture page for the SlackThreadCard + EmailPreviewCard
// e2e specs. Mounts the cards with synthetic results and exposes a
// `__commsTest` window hook so Playwright can drive them without an
// MCP server.
//
// This page is dev-only — not linked from the app shell. It exists so the
// e2e specs can verify the specialised tool-card render path WITHOUT
// requiring the full brain-agent / MCP / Slack-stealth stack.

import { createSignal, type JSX } from "solid-js";
import SlackThreadCard from "../../components/comms/SlackThreadCard";
import EmailPreviewCard from "../../components/comms/EmailPreviewCard";

interface CommsTestHook {
  setSlack: (result: unknown, toolName?: string) => void;
  setMail: (result: unknown, toolName?: string) => void;
  /** The most recent save_to_vault payload the snapshot button dispatched. */
  lastSaveToVaultArgs: Record<string, unknown> | null;
  saveToVaultCallCount: number;
}

const SLACK_FIXTURE = {
  display_hint: "slack-thread" as const,
  channel: { id: "C01", name: "general", type: "public_channel", is_private: false },
  channel_name: "general",
  messages: [
    {
      ts: "1700000000.000100",
      user: "U001",
      user_profile: { name: "alice", real_name: "Alice Doe" },
      text: "Hey *team*, reviewing the <@U002> proposal at <#C99|engineering>. See https://example.com",
      reactions: [{ name: "thumbsup", count: 2 }, { name: "eyes", count: 1 }],
      reply_count: 3,
    },
    {
      ts: "1700000060.000200",
      user: "U002",
      user_profile: { name: "bob", real_name: "Bob Smith" },
      text: "On it. _Will ping_ <!here> when ready.",
      thread_ts: "1700000000.000100",
    },
  ],
};

const MAIL_FIXTURE = {
  display_hint: "email-preview" as const,
  message: {
    id: "msg-abc-123",
    threadId: "thread-xyz-456",
    subject: "Q2 review draft — please look",
    from: "alice@example.com",
    to: "you@example.com",
    date: new Date().toISOString(),
    snippet: "Attaching the Q2 doc — main points on slide 3 and slide 12.",
    body:
      "Attaching the Q2 doc — main points on slide 3 and slide 12.\n\n" +
      "Ping me with anything that doesn't track. We can sync tomorrow afternoon.\n\n" +
      "— A",
    labels: ["INBOX", "UNREAD", "IMPORTANT"],
    attachments: [
      {
        id: "att-1",
        attachmentId: "att-1",
        filename: "q2-review.pdf",
        mimeType: "application/pdf",
        size: 184_320,
      },
    ],
  },
};

export default function DevComms(): JSX.Element {
  const [slackResult, setSlackResult] = createSignal<unknown>(SLACK_FIXTURE);
  const [slackToolName, setSlackToolName] = createSignal("slack.read_channel");
  const [mailResult, setMailResult] = createSignal<unknown>(MAIL_FIXTURE);
  const [mailToolName, setMailToolName] = createSignal("mail.read");

  // Install the test hook + the dispatch stub. The CommsSnapshotButton
  // checks `globalThis.__tetherMcpDispatch` first, so we intercept here
  // and accumulate calls instead of opening a real WS.
  const w = window as unknown as {
    __commsTest?: CommsTestHook;
    __tetherMcpDispatch?: (
      toolName: string,
      args: Record<string, unknown>,
    ) => Promise<unknown>;
  };
  const hook: CommsTestHook = {
    setSlack: (result, toolName) => {
      setSlackResult(result);
      if (toolName) setSlackToolName(toolName);
    },
    setMail: (result, toolName) => {
      setMailResult(result);
      if (toolName) setMailToolName(toolName);
    },
    lastSaveToVaultArgs: null,
    saveToVaultCallCount: 0,
  };
  w.__commsTest = hook;
  w.__tetherMcpDispatch = async (toolName, args) => {
    if (toolName === "save_to_vault") {
      hook.lastSaveToVaultArgs = args;
      hook.saveToVaultCallCount += 1;
      return {
        saved: `${(args.location as string) ?? "inbox/"}${(args.filename as string) ?? "x"}.md`,
        absolutePath: `/tmp/test-vault/${(args.filename as string) ?? "x"}.md`,
      };
    }
    throw new Error(`unmocked tool: ${toolName}`);
  };

  return (
    <div
      style={{
        padding: "32px 24px 96px",
        "max-width": "640px",
        margin: "0 auto",
        display: "flex",
        "flex-direction": "column",
        gap: "24px",
      }}
    >
      <p class="kicker">internal · comms-card fixtures</p>
      <h1 style={{ "font-size": "32px", "margin-top": "0" }}>/dev/comms</h1>
      <p style={{ "font-size": "13px", color: "#9a9ab0" }}>
        Fixture page used by <code>e2e/comms-cards.spec.ts</code>. Drive the cards
        via <code>window.__commsTest.setSlack(...)</code> and{" "}
        <code>window.__commsTest.setMail(...)</code>.
      </p>

      <section data-testid="slack-fixture">
        <h2 style={{ "font-size": "14px", "letter-spacing": "0.06em", "text-transform": "uppercase", color: "#9a9ab0" }}>
          slack
        </h2>
        <SlackThreadCard result={slackResult()} toolName={slackToolName()} />
      </section>

      <section data-testid="mail-fixture">
        <h2 style={{ "font-size": "14px", "letter-spacing": "0.06em", "text-transform": "uppercase", color: "#9a9ab0" }}>
          mail
        </h2>
        <EmailPreviewCard result={mailResult()} toolName={mailToolName()} />
      </section>
    </div>
  );
}
